//C:\Users\Anish\xpr_survey\XPRSuvey-App\src\Utility\RichTextEditor.jsx

import React, { useCallback, useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyleKit } from "@tiptap/extension-text-style";
import { TableKit } from "@tiptap/extension-table";
import Placeholder from "@tiptap/extension-placeholder";
import { Extension } from "@tiptap/core";
import { OrderedList } from "@tiptap/extension-list";

import {
    Bold,
    Underline as UnderlineIcon,
    List,
    ListOrdered,
    Heading1,
    Heading2,
    Heading3,
    Table as TableIcon,
    Rows3,
    Columns3,
    Trash2,
    Undo2,
    Redo2,
} from "lucide-react";

const FONT_SIZES = ["12px", "14px", "16px", "18px", "20px", "24px", "28px", "32px"];

const FontSize = Extension.create({
    name: "fontSize",
    addOptions() {
        return { types: ["textStyle"] };
    },
    addGlobalAttributes() {
        return [
            {
                types: this.options.types,
                attributes: {
                    fontSize: {
                        default: null,
                        parseHTML: (element) => element.style.fontSize.replace(/['"]+/g, "") || null,
                        renderHTML: (attributes) => {
                            if (!attributes.fontSize) return {};
                            return { style: `font-size: ${attributes.fontSize}` };
                        },
                    },
                },
            },
        ];
    },
    addCommands() {
        return {
            setFontSize: (fontSize) => ({ chain }) => chain().setMark("textStyle", { fontSize }).run(),
            unsetFontSize: () => ({ chain }) => chain().setMark("textStyle", { fontSize: null }).removeEmptyTextStyle().run(),
        };
    },
});

const OrderedListWithType = OrderedList.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            type: {
                default: null,
                parseHTML: (element) => element.getAttribute("type"),
                renderHTML: (attributes) => (attributes.type ? { type: attributes.type } : {}),
            },
        };
    },
});

const ToolbarButton = ({ onClick, active, disabled, title, children }) => (
    <button
        type="button"
        onMouseDown={(event) => event.preventDefault()}
        onClick={onClick}
        disabled={disabled}
        title={title}
        className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-gray-600 transition disabled:cursor-not-allowed disabled:opacity-30 ${active ? "bg-[#7f35b2]/15 text-[#7f35b2]" : "hover:bg-gray-100"
            }`}
    >
        {children}
    </button>
);

const RichTextEditor = ({
    initialContent = "",
    onChange,
    placeholder,
    minHeight = 180,
    editable = true,
    enableTable = true, // set false for consumers that shouldn't get table insertion
}) => {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: { levels: [1, 2, 3] },
                orderedList: false,
            }),
            OrderedListWithType,
            FontSize,
            TextStyleKit.configure({
                color: false,
                fontFamily: false,
                backgroundColor: false,
                lineHeight: false,
            }),
            ...(enableTable ? [TableKit] : []),
            Placeholder.configure({
                placeholder: placeholder || "Write the passage here...",
            }),
        ],
        content: initialContent,
        editable,
        onUpdate: ({ editor: currentEditor }) => {
            onChange?.(currentEditor.getHTML());
        },
        editorProps: {
            attributes: {
                class: "rte-content px-4 py-3 text-sm text-gray-800 outline-none",
                style: `min-height: ${minHeight}px`,
            },
        },
    });

    useEffect(() => {
        if (editor && editor.isEditable !== editable) {
            editor.setEditable(editable);
        }
    }, [editable, editor]);

    const setFontSize = useCallback(
        (size) => {
            if (!editor) return;
            if (size === "default") {
                editor.chain().focus().unsetFontSize().run();
            } else {
                editor.chain().focus().setFontSize(size).run();
            }
        },
        [editor],
    );

    const setOrderedListType = useCallback(
        (type) => {
            if (!editor) return;
            const isSameType =
                editor.isActive("orderedList") &&
                (editor.getAttributes("orderedList").type || null) === type;

            if (isSameType) {
                editor.chain().focus().toggleOrderedList().run(); // remove the list
            } else if (editor.isActive("orderedList")) {
                editor.chain().focus().updateAttributes("orderedList", { type }).run(); // switch type in place
            } else {
                editor.chain().focus().toggleOrderedList().updateAttributes("orderedList", { type }).run(); // create new
            }
        },
        [editor],
    );

    if (!editor) return null;

    const isInsideTable = enableTable && editor.isActive("table");

    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white focus-within:border-[#7f35b2] focus-within:ring-2 focus-within:ring-[#7f35b2]/20">
            {editable && (
                <div className="flex flex-wrap items-center gap-1 border-b border-gray-100 bg-gray-50 px-2 py-1.5">
                    <ToolbarButton
                        title="Undo"
                        onClick={() => editor.chain().focus().undo().run()}
                        disabled={!editor.can().undo()}
                    >
                        <Undo2 size={15} />
                    </ToolbarButton>
                    <ToolbarButton
                        title="Redo"
                        onClick={() => editor.chain().focus().redo().run()}
                        disabled={!editor.can().redo()}
                    >
                        <Redo2 size={15} />
                    </ToolbarButton>

                    <div className="mx-1 h-5 w-px bg-gray-200" />

                    <ToolbarButton
                        title="Heading 1"
                        active={editor.isActive("heading", { level: 1 })}
                        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                    >
                        <Heading1 size={15} />
                    </ToolbarButton>
                    <ToolbarButton
                        title="Heading 2"
                        active={editor.isActive("heading", { level: 2 })}
                        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    >
                        <Heading2 size={15} />
                    </ToolbarButton>
                    <ToolbarButton
                        title="Heading 3"
                        active={editor.isActive("heading", { level: 3 })}
                        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                    >
                        <Heading3 size={15} />
                    </ToolbarButton>

                    <div className="mx-1 h-5 w-px bg-gray-200" />

                    <ToolbarButton
                        title="Bold"
                        active={editor.isActive("bold")}
                        onClick={() => editor.chain().focus().toggleBold().run()}
                    >
                        <Bold size={15} />
                    </ToolbarButton>
                    <ToolbarButton
                        title="Underline"
                        active={editor.isActive("underline")}
                        onClick={() => editor.chain().focus().toggleUnderline().run()}
                    >
                        <UnderlineIcon size={15} />
                    </ToolbarButton>

                    <select
                        value={editor.getAttributes("textStyle").fontSize || "default"}
                        onChange={(event) => setFontSize(event.target.value)}
                        className="ml-1 rounded-md border border-gray-200 bg-white px-1.5 py-1 text-xs text-gray-600 outline-none focus:border-[#7f35b2]"
                        title="Font size"
                    >
                        <option value="default">Size</option>
                        {FONT_SIZES.map((size) => (
                            <option key={size} value={size}>
                                {size}
                            </option>
                        ))}
                    </select>

                    <div className="mx-1 h-5 w-px bg-gray-200" />

                    <ToolbarButton
                        title="Bullet list"
                        active={editor.isActive("bulletList")}
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                    >
                        <List size={15} />
                    </ToolbarButton>
                    <ToolbarButton
                        title="Numbered list"
                        active={editor.isActive("orderedList") && editor.getAttributes("orderedList").type !== "a"}
                        onClick={() => setOrderedListType(null)}
                    >
                        <ListOrdered size={15} />
                    </ToolbarButton>

                    <ToolbarButton
                        title="Alphabetical list (a, b, c)"
                        active={editor.isActive("orderedList") && editor.getAttributes("orderedList").type === "a"}
                        onClick={() => setOrderedListType("a")}
                    >
                        <span className="text-[11px] font-bold leading-none">a-z</span>
                    </ToolbarButton>

                    {enableTable && (
                        <>
                            <div className="mx-1 h-5 w-px bg-gray-200" />

                            <ToolbarButton
                                title="Insert table"
                                onClick={() =>
                                    editor.chain().focus().insertTable({ rows: 2, cols: 1, withHeaderRow: true }).run()
                                }
                            >
                                <TableIcon size={15} />
                            </ToolbarButton>

                            {isInsideTable && (
                                <>
                                    <ToolbarButton title="Add row" onClick={() => editor.chain().focus().addRowAfter().run()}>
                                        <Rows3 size={15} />
                                    </ToolbarButton>
                                    <ToolbarButton
                                        title="Add column"
                                        onClick={() => editor.chain().focus().addColumnAfter().run()}
                                    >
                                        <Columns3 size={15} />
                                    </ToolbarButton>
                                    <ToolbarButton
                                        title="Delete table"
                                        onClick={() => editor.chain().focus().deleteTable().run()}
                                    >
                                        <Trash2 size={15} />
                                    </ToolbarButton>
                                </>
                            )}
                        </>
                    )}
                </div>
            )}

            <EditorContent editor={editor} />

            <style>{`
        .rte-content .ProseMirror { outline: none; }
        .rte-content h1 { font-size: 1.5rem; font-weight: 700; margin: 0.5rem 0; }
        .rte-content h2 { font-size: 1.25rem; font-weight: 700; margin: 0.5rem 0; }
        .rte-content h3 { font-size: 1.1rem; font-weight: 600; margin: 0.5rem 0; }
        .rte-content ul { list-style: disc; padding-left: 1.25rem; }
         .rte-content ol { list-style: decimal; padding-left: 1.25rem; }
        .rte-content ol[type="a"] { list-style-type: lower-alpha; }
        .rte-content ol[type="A"] { list-style-type: upper-alpha; }
        .rte-content table { border-collapse: collapse; width: 100%; margin: 0.5rem 0; table-layout: fixed; }
        .rte-content td, .rte-content th { border: 1px solid #e2e8f0; padding: 6px 10px; position: relative; vertical-align: top; }
        .rte-content th { background: #f8fafc; font-weight: 600; text-align: left; }
        .rte-content p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          color: #9ca3af;
          float: left;
          height: 0;
          pointer-events: none;
        }
      `}</style>
        </div>
    );
};

export default RichTextEditor;
